import React, { useState } from 'react';
import {
    Box, Typography, Paper, Chip, Accordion, AccordionSummary, AccordionDetails,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tooltip,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    InfoOutlined as InfoIcon,
} from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Acceptance colour helpers
// ---------------------------------------------------------------------------

function acceptanceColor(status) {
    return { acceptable: '#81c784', conditional: '#ffb74d', unacceptable: '#e57373' }[status] || '#90caf9';
}

function AcceptanceChip({ status, value, label }) {
    const color = acceptanceColor(status);
    const Icon  = { acceptable: CheckCircleIcon, conditional: WarningIcon, unacceptable: ErrorIcon }[status] || InfoIcon;
    const text  = { acceptable: 'Acceptable', conditional: 'Conditional', unacceptable: 'Unacceptable' }[status] || 'N/A';
    return (
        <Chip
            icon={<Icon sx={{ fontSize: '1rem !important', color: `${color} !important` }} />}
            label={label ? `${label}: ${text}` : text}
            sx={{
                backgroundColor: `${color}18`,
                border: `1px solid ${color}55`,
                color,
                fontWeight: 600,
                fontSize: '0.8rem',
            }}
        />
    );
}

// ---------------------------------------------------------------------------
// Metric chip — inline status indicator for table cells
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Interpretation panel
// ---------------------------------------------------------------------------

function InterpretationPanel({ interpretation }) {
    if (!interpretation) return null;
    const { summary, recommendations, ndcText } = interpretation;
    return (
        <Paper sx={{ p: 2, backgroundColor: 'rgba(144,202,249,0.06)', border: '1px solid rgba(144,202,249,0.15)', borderRadius: 1, mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, mb: ndcText || recommendations?.length ? 1.5 : 0 }}>
                {summary}
            </Typography>
            {ndcText && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, mb: recommendations?.length ? 1 : 0 }}>
                    {ndcText}
                </Typography>
            )}
            {recommendations?.length > 0 && (
                <Box>
                    <Typography variant="caption" sx={{ color: '#90caf9', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Recommendations
                    </Typography>
                    {recommendations.map((r, i) => (
                        <Typography key={i} variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, mt: 0.5, pl: 1, borderLeft: '2px solid rgba(144,202,249,0.3)' }}>
                            {r}
                        </Typography>
                    ))}
                </Box>
            )}
        </Paper>
    );
}

// ---------------------------------------------------------------------------
// Table helpers
// ---------------------------------------------------------------------------

const headCellSx = {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '0.72rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    py: 1,
    px: 1.5,
    whiteSpace: 'nowrap',
};

const bodyCellSx = {
    color: 'rgba(255,255,255,0.8)',
    fontSize: '0.82rem',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    py: 0.75,
    px: 1.5,
};

function fmt(v, decimals = 6) {
    if (v === null || v === undefined || !isFinite(v)) return '—';
    return Number(v).toFixed(decimals);
}

function fmtPct(v) {
    if (v === null || v === undefined || !isFinite(v)) return '—';
    return `${Number(v).toFixed(2)}%`;
}

function pValueText(p) {
    if (p === null || p === undefined) return '—';
    if (p < 0.0001) return '< 0.0001';
    return Number(p).toFixed(4);
}

// ---------------------------------------------------------------------------
// ANOVA Table
// ---------------------------------------------------------------------------

function ANOVATable({ anovaTable }) {
    if (!anovaTable?.length) return null;

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {['Source', 'df', 'SS', 'MS', 'F', 'p-Value'].map(h => (
                            <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {anovaTable.map((row, i) => {
                        const isTotal = row.source === 'Total';
                        return (
                            <TableRow
                                key={i}
                                sx={{
                                    backgroundColor: isTotal ? 'rgba(255,255,255,0.03)' : 'transparent',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
                                }}
                            >
                                <TableCell sx={{ ...bodyCellSx, fontWeight: isTotal ? 600 : 400 }}>
                                    {row.source}
                                    {row.pooled && (
                                        <Chip label="pooled" size="small" sx={{ ml: 1, height: 16, fontSize: '0.6rem', backgroundColor: 'rgba(255,183,77,0.15)', color: '#ffb74d' }} />
                                    )}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>{row.df ?? '—'}</TableCell>
                                <TableCell sx={bodyCellSx}>{fmt(row.ss)}</TableCell>
                                <TableCell sx={bodyCellSx}>{fmt(row.ms)}</TableCell>
                                <TableCell sx={bodyCellSx}>{row.f !== null && row.f !== undefined ? fmt(row.f, 4) : '—'}</TableCell>
                                <TableCell sx={{ ...bodyCellSx, color: row.pValue < 0.05 ? '#81c784' : 'rgba(255,255,255,0.8)' }}>
                                    {pValueText(row.pValue)}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// ---------------------------------------------------------------------------
// Variance Components Table
// ---------------------------------------------------------------------------

function VarianceComponentsTable({ varianceComponents, hasTolerance }) {
    if (!varianceComponents?.length) return null;

    const headers = ['Source', 'VarComp', '%Contribution', 'StdDev', 'Study Var (6σ)', '%Study Var'];
    if (hasTolerance) headers.push('%Tolerance');

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {headers.map(h => (
                            <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {varianceComponents.map((row, i) => {
                        const isTotal   = row.source === 'Total Variation';
                        const isGRR     = row.source === 'Total GRR';
                        const indentPx  = (row.indent || 0) * 16;
                        const pctContrib= row.pctContribution;

                        // Colour code %Contribution
                        let contribColor = 'rgba(255,255,255,0.8)';
                        if (isGRR && pctContrib !== null) {
                            if (pctContrib < 10)       contribColor = '#81c784';
                            else if (pctContrib <= 30)  contribColor = '#ffb74d';
                            else                        contribColor = '#e57373';
                        }

                        return (
                            <TableRow
                                key={i}
                                sx={{
                                    backgroundColor: (isTotal || isGRR) ? 'rgba(255,255,255,0.03)' : 'transparent',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
                                }}
                            >
                                <TableCell sx={{ ...bodyCellSx, pl: `${12 + indentPx}px`, fontWeight: (isTotal || isGRR) ? 600 : 400 }}>
                                    {row.source}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>{fmt(row.variance)}</TableCell>
                                <TableCell sx={{ ...bodyCellSx, color: contribColor, fontWeight: isGRR ? 600 : 400 }}>
                                    {fmtPct(pctContrib)}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>{fmt(row.stdDev)}</TableCell>
                                <TableCell sx={bodyCellSx}>{fmt(row.studyVar6Sigma)}</TableCell>
                                <TableCell sx={bodyCellSx}>{fmtPct(row.pctStudyVar)}</TableCell>
                                {hasTolerance && (
                                    <TableCell sx={bodyCellSx}>{fmtPct(row.pctTolerance)}</TableCell>
                                )}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// ---------------------------------------------------------------------------
// Key metrics summary bar
// ---------------------------------------------------------------------------

function GRRSummaryBar({ grrMetrics, studyDesign }) {
    if (!grrMetrics) return null;
    const { pctGRR, pctGRRTolerance, ndc, acceptance } = grrMetrics;

    const metrics = [
        { label: '%GRR (Study Var)', value: pctGRR !== null ? `${pctGRR?.toFixed(2)}%` : '—', status: acceptance },
        { label: '%GRR (Tolerance)', value: pctGRRTolerance !== null ? `${pctGRRTolerance?.toFixed(2)}%` : '—', status: null },
        { label: 'NDC', value: ndc !== null ? ndc : '—', status: ndc !== null ? (ndc >= 5 ? 'acceptable' : 'unacceptable') : null, tooltip: 'Number of Distinct Categories — must be ≥ 5 for adequate discrimination' },
        { label: 'Interaction Pooled', value: studyDesign?.poolInteraction ? 'Yes' : 'No', status: null },
    ];

    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, p: 1.5, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}>
            {metrics.map(({ label, value, status, tooltip }) => (
                <Box key={label} sx={{ minWidth: 100 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', fontSize: '0.68rem', mb: 0.25 }}>
                        {label}
                        {tooltip && (
                            <Tooltip title={tooltip} arrow placement="top">
                                <InfoIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle', color: 'rgba(255,255,255,0.3)' }} />
                            </Tooltip>
                        )}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: status ? acceptanceColor(status) : 'rgba(255,255,255,0.9)', fontSize: '1rem' }}>
                        {value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function GRRResultsSection({ results }) {
    const [anovaExpanded, setAnovaExpanded]     = useState(true);
    const [vcExpanded, setVcExpanded]           = useState(true);

    if (!results) return null;

    const { anovaTable, varianceComponents, grrMetrics, studyDesign, interpretation } = results;
    const hasTolerance = varianceComponents?.some(r => r.pctTolerance !== null && r.pctTolerance !== undefined);

    return (
        <Box>
            {/* Acceptance status headline */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <AcceptanceChip status={grrMetrics?.acceptance} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                    {studyDesign && `${studyDesign.numParts} parts × ${studyDesign.numOperators} operators × ${studyDesign.numTrials} trials`}
                </Typography>
            </Box>

            {/* Summary bar */}
            <GRRSummaryBar grrMetrics={grrMetrics} studyDesign={studyDesign} />

            {/* Interpretation */}
            <InterpretationPanel interpretation={interpretation} />

            {/* Variance Components Table */}
            <Accordion
                expanded={vcExpanded}
                onChange={() => setVcExpanded(v => !v)}
                sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', mb: 1.5, '&:before': { display: 'none' } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Variance Components</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <VarianceComponentsTable varianceComponents={varianceComponents} hasTolerance={hasTolerance} />
                </AccordionDetails>
            </Accordion>

            {/* ANOVA Table */}
            <Accordion
                expanded={anovaExpanded}
                onChange={() => setAnovaExpanded(v => !v)}
                sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', '&:before': { display: 'none' } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Two-Way ANOVA</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <ANOVATable anovaTable={anovaTable} />
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}

export default GRRResultsSection;
